/**
 * Helper SEO bersama (sitemap, robots, JSON-LD, canonical & Open Graph halaman
 * publik). MURNI — tanpa React/Supabase/I-O (import `next` di bawah type-only,
 * terhapus saat kompilasi). Diuji di `__tests__/seo.test.ts`.
 *
 * JSON-LD halaman PRODUK tidak di sini: `productJsonLd.ts` — penawarannya
 * (harga, ketersediaan, URL varian) dibagi dengan feed Katalog Instagram.
 */

import type { Metadata } from "next";

/** Buang spasi & trailing slash supaya aman digabung dengan path. */
function trimBase(raw: string): string {
  return raw.trim().replace(/\/+$/, "");
}

/**
 * Base URL absolut situs. Prioritas: `NEXT_PUBLIC_SITE_URL` → `SITE_URL` →
 * localhost (dev). Tanpa trailing slash.
 *
 * ⚠️ `NEXT_PUBLIC_SITE_URL` di-INLINE saat BUILD. Kalau env ini kosong di host,
 * sitemap + robots + canonical + JSON-LD memakai `http://localhost:3000`.
 * Mengubah nilainya di panel TANPA build ulang tidak berefek apa pun.
 */
export function siteBaseUrl(): string {
  const raw =
    process.env.NEXT_PUBLIC_SITE_URL ||
    process.env.SITE_URL ||
    "http://localhost:3000";
  return trimBase(raw);
}

/** Pola hostname sah: label alfanumerik dipisah titik, ditutup TLD huruf. */
const HOSTNAME_RE =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;

/**
 * Base URL **KANONIK** sebuah toko.
 *
 * Sumber kebenaran alamat sebuah toko adalah `stores.custom_domain` (Fase 6);
 * selama kolom itu kosong dipakai `fallback` (env situs). Nilai yang bukan
 * hostname sah SELALU jatuh ke `fallback` — fail-safe, supaya salah isi di DB
 * tak pernah merusak canonical seluruh situs.
 *
 * Menerima `example.com` maupun `https://example.com/`.
 *
 * CATATAN: sengaja TIDAK memakai Host request. Canonical harus menunjuk satu
 * alamat tetap per toko; kalau ia mengikuti host pengunjung, tiap domain alias
 * akan mengkanonikkan dirinya sendiri dan duplikatnya tak pernah menyatu.
 */
export function storeCanonicalBase(
  customDomain: string | null | undefined,
  fallback: string
): string {
  const fb = trimBase(fallback ?? "") || "http://localhost:3000";
  const host = (customDomain ?? "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/:\d+$/, "")
    .replace(/\.+$/, "");
  if (!host || !HOSTNAME_RE.test(host)) return fb;
  return `https://${host}`;
}

/**
 * `metadataBase` untuk Next Metadata API. Tanpa ini Next tak bisa memancarkan
 * `<link rel="canonical">` / `og:url` absolut. Mengembalikan `undefined` bila
 * env salah bentuk — lebih baik canonical hilang daripada render melempar.
 */
export function metadataBaseUrl(base: string = siteBaseUrl()): URL | undefined {
  try {
    return new URL(trimBase(base));
  } catch {
    return undefined;
  }
}

/** Gabung base + path → URL absolut. `path` boleh diawali "/" atau tidak. */
export function absoluteUrl(path: string, base: string = siteBaseUrl()): string {
  const b = trimBase(base);
  const p = (path ?? "").trim();
  if (!p || p === "/") return `${b}/`;
  return `${b}${p.startsWith("/") ? "" : "/"}${p}`;
}

/**
 * Path canonical halaman berfaset (`/kategori?cat=…`, `/merek?brand=…`).
 *
 * Hanya SATU parameter — yang pertama terisi menurut urutan argumen — yang
 * dipertahankan. Tujuannya: kombinasi filter (cat+segment+brand+type) tidak
 * melahirkan URL kanonik baru, jadi situs tak memproduksi duplikatnya sendiri.
 * Cermin daftar URL di `src/app/sitemap.ts`.
 */
export function canonicalPath(
  basePath: string,
  params?: Array<readonly [string, string | null | undefined]>
): string {
  const p = basePath.startsWith("/") ? basePath : `/${basePath}`;
  for (const [key, value] of params ?? []) {
    const v = (value ?? "").trim();
    if (v) return `${p}?${key}=${encodeURIComponent(v)}`;
  }
  return p;
}

/**
 * Apakah nilai faset katalog (`?cat=`, `?segment=`, `?brand=`) ASING bagi
 * master toko — yaitu halaman akan merender layar "tidak ditemukan".
 *
 * KENAPA PERLU DI LAPISAN METADATA: layar "tidak ditemukan" itu dirender
 * komponen KLIEN, sementara `generateMetadata` memutuskan sendiri dan tak
 * tahu apa-apa soal itu. Akibatnya `?cat=NgawurApaPun` memulangkan HTTP 200 +
 * judul yang memantulkan string mentahnya + canonical yang menunjuk BALIK ke
 * URL ngawur itu — soft-404 yang mengundang dirinya sendiri masuk indeks.
 * Diverifikasi live 2026-09-21 di zzhomey.com & galeastore.id.
 *
 * ⚠️ FAIL-OPEN saat `masterNames` kosong, dan ini bukan kelalaian.
 * `cachedForStore` (catalog-cache.ts) memulangkan `[]` ketika resolusi toko
 * gagal — bukan hanya ketika toko benar-benar tak punya kategori. Tanpa
 * gerbang ini, satu RPC yang ngadat membuat SELURUH URL faset toko serentak
 * menyandang `noindex`, dan Google MEMATUHINYA: seluruh permukaan kategori
 * bisa tercabut dari indeks karena gangguan sesaat, dan pulihnya berminggu-
 * minggu. Arah gagal yang sama persis dengan `isStoreSuspended` di
 * `src/app/robots.ts` — di kelas keputusan ini, diam lebih murah daripada
 * salah melarang.
 */
export function isUnknownFacetValue(
  value: string | null | undefined,
  masterNames: readonly string[]
): boolean {
  const wanted = (value ?? "").trim().toLowerCase();
  // Tanpa faset = halaman landing; selalu layak indeks.
  if (!wanted) return false;
  // Master tak terbaca → jangan memutuskan apa pun (lihat FAIL-OPEN di atas).
  if (masterNames.length === 0) return false;
  // Case-insensitive + trim, cermin `normalize()` di KategoriClient/MerekClient:
  // `?cat=gamis` dan `?cat=Gamis` sama-sama sah, jadi tak boleh di-noindex.
  return !masterNames.some((name) => (name ?? "").trim().toLowerCase() === wanted);
}

type OpenGraphMeta = NonNullable<Metadata["openGraph"]>;
/**
 * `Omit` yang DIDISTRIBUSIKAN atas union varian OpenGraph (website/article/…).
 * `Omit` biasa atas union hanya menyisakan properti irisan semua varian —
 * field khas varian (`type: "website"` dst) ikut hilang dari tipe parameter.
 */
type DistributedOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

/**
 * Blok canonical + Open Graph UTUH untuk satu halaman publik.
 * `alternates.canonical` dan `openGraph.url` DIJAMIN identik — keduanya lahir
 * dari SATU nilai, supaya og:url tak bisa hilang sendirian lagi.
 *
 * Menerima payload OG LENGKAP (tanpa `url`) dan mengembalikan objek utuh, karena
 * metadata Next di-SHALLOW-MERGE: `openGraph` parsial di segmen anak MENIMPA
 * milik induk — halaman yang memakai helper ini wajib menyertakan
 * type/siteName/locale/images sendiri, tidak lagi mewarisi dari layout root.
 *
 * `path` = path kanonik halaman (boleh sudah berfaset, mis. hasil
 * `canonicalPath("/merek", [["brand", label]])`) — dinormalkan lalu
 * diabsolutkan ke `siteBaseUrl()`.
 */
export function canonicalOpenGraph(
  path: string,
  og: DistributedOmit<OpenGraphMeta, "url">,
  base?: string
): Pick<Metadata, "alternates" | "openGraph"> {
  // 【6】: `base` = alamat kanonik TOKO (dari `stores`, lewat
  // resolveCanonicalBase()). Tanpa argumen → env situs = perilaku lama, jadi
  // pemanggil yang belum di-thread tetap benar untuk deployment satu toko.
  const url = absoluteUrl(canonicalPath(path), base ?? siteBaseUrl());
  return {
    alternates: { canonical: url },
    openGraph: { ...og, url } as OpenGraphMeta,
  };
}

/**
 * Serialisasi JSON-LD yang AMAN di-inject lewat `dangerouslySetInnerHTML`.
 *
 * `<` di-escape jadi `\u003c` — nilai di dalam JSON-LD datang dari input admin
 * (nama kategori, merek, judul produk, deskripsi). Tanpa escape ini sebuah nama
 * berisi `</script>` menutup blok JSON-LD lebih awal dan sisanya dieksekusi
 * browser sebagai HTML. JSON tetap sah: `\u003c` adalah `<` yang sama.
 */
export function jsonLdText(value: unknown): string {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

/**
 * JSON-LD `BreadcrumbList` (string siap di-inject ke <script>).
 *
 * `base` WAJIB dan TANPA nilai bawaan — itu perbaikan bug produksi, bukan
 * gaya. Dulu fungsi ini memanggil `siteBaseUrl()` sendiri, dan karena
 * `NEXT_PUBLIC_SITE_URL` kosong di kedua website Hostinger, SETIAP breadcrumb
 * di SEMUA toko terbit sebagai `http://localhost:3000/...` (terverifikasi live
 * 2026-09-11 di galeastore.id/promo dan zzhomey.com/kategori).
 *
 * Mengisi env itu BUKAN obatnya: `siteBaseUrl()` satu nilai global untuk semua
 * host, jadi hasilnya cuma berganti jadi domain toko yang salah. Alamat toko
 * ini harus datang dari `resolveCanonicalBase()` — sumber yang sama dengan
 * canonical & og:url halamannya. Parameter wajib membuat pemanggil yang lupa
 * jadi galat kompilasi, bukan bug senyap yang baru ketahuan dari Search Console.
 */
export function breadcrumbJsonLd(
  trail: Array<{ name: string; path: string }>,
  base: string
): string {
  const b = trimBase(base);
  return jsonLdText({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${b}${item.path}`,
    })),
  });
}

/**
 * JSON-LD identitas TOKO: `Organization` + `WebSite`.
 *
 * `WebSite.potentialAction` mendaftarkan kotak cari toko ke Google — syarat
 * sitelinks searchbox, dan sekaligus memberi tahu mesin pencari bahwa
 * `/cari?q=` adalah pencarian, bukan halaman duplikat.
 *
 * `@id` ber-fragment (`#organization`, `#website`) memakai base TOKO INI, jadi
 * dua toko tak pernah berbagi identitas graph yang sama.
 */
export function storeJsonLd(input: {
  storeName: string;
  base: string;
  description: string;
  logoUrl?: string | null;
}): string {
  const b = trimBase(input.base);
  const url = `${b}/`;
  return jsonLdText([
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${b}/#organization`,
      name: input.storeName,
      url,
      ...(input.logoUrl ? { logo: input.logoUrl } : {}),
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${b}/#website`,
      name: input.storeName,
      url,
      description: input.description,
      inLanguage: "id-ID",
      publisher: { "@id": `${b}/#organization` },
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${b}/cari?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    },
  ]);
}

/**
 * JSON-LD `ItemList` untuk halaman daftar (kategori, merek, rail).
 *
 * Memberi mesin pencari urutan produk yang terlihat di halaman — tanpa ini
 * halaman daftar terbaca sebagai kumpulan tautan tanpa struktur, dan
 * produk-produknya hanya ditemukan lewat sitemap.
 */
export function itemListJsonLd(input: {
  name: string;
  base: string;
  /** Path RELATIF tiap item, mis. `/product/slug-produk`. */
  items: ReadonlyArray<{ name: string; path: string }>;
}): string {
  const b = trimBase(input.base);
  return jsonLdText({
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: input.name,
    numberOfItems: input.items.length,
    itemListElement: input.items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      url: `${b}${item.path}`,
    })),
  });
}

/**
 * Batas aman judul & deskripsi di hasil pencarian.
 *
 * Google memotong yang lewat batas ini DI TENGAH KALIMAT — potongan itulah yang
 * dibaca calon pembeli, jadi angkanya bukan sekadar saran gaya. Nilainya
 * perkiraan (Google mengukur PIKSEL, bukan karakter), sengaja dipasang sedikit
 * konservatif. Dipakai bersama oleh kolom admin (penghitung + pratinjau) dan
 * `clampText` di bawah — satu sumber, supaya yang dijanjikan layar sama dengan
 * yang benar-benar terbit.
 */
export const SEO_TITLE_MAX = 60;
export const SEO_DESCRIPTION_MAX = 160;

/**
 * Potong teks di BATAS KATA, tutup dengan elipsis. Lebih pendek dari `max` →
 * dikembalikan apa adanya.
 *
 * Memotong di tengah kata ("Grosir gamis sya…") terbaca seperti halaman rusak;
 * memotong di spasi terakhir sebelum batas jauh lebih rapi. Kalau tak ada spasi
 * sama sekali (satu kata sangat panjang) barulah dipotong keras.
 */
export function clampText(raw: string, max: number): string {
  const text = (raw ?? "").replace(/\s+/g, " ").trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * Judul situs toko. Isian admin MENGGANTIKAN seluruhnya; kosong = nama toko
 * (perilaku sebelum fitur ini ada).
 *
 * Mengganti, bukan menambah: judul adalah baris paling berharga di hasil
 * pencarian, dan menempelkan nama toko ke judul yang sudah ditulis pemiliknya
 * hanya memakan jatah karakternya.
 */
export function storeSeoTitle(
  custom: string | null | undefined,
  storeName: string
): string {
  const text = (custom ?? "").trim();
  return text ? clampText(text, SEO_TITLE_MAX) : storeName;
}

/**
 * Deskripsi situs toko (beranda). Isian admin MENGGANTIKAN seluruhnya; kosong =
 * rumus bawaan — sama persis dengan yang terbit sebelum fitur ini ada, jadi
 * toko yang tak mengisi apa pun tidak berubah sedikit pun.
 */
export function storeSeoDescription(
  custom: string | null | undefined,
  storeName: string
): string {
  const text = (custom ?? "").trim();
  return text
    ? clampText(text, SEO_DESCRIPTION_MAX)
    : `Belanja preorder & ready stock ${storeName}`;
}

/**
 * Deskripsi HALAMAN DALAM (kategori, merek, cari, …).
 *
 * Berbeda dari beranda, isian admin di sini MENAMBAH, tidak mengganti:
 * `specific` adalah kalimat khas halaman itu ("Belanja produk Gamis di …") dan
 * itulah yang membuat tiap halaman punya deskripsi BERBEDA. Deskripsi yang
 * seragam di ratusan URL justru sinyal buruk — Google memperlakukannya sebagai
 * halaman kembar. Jadi kalimat khasnya selalu di depan, kata-kata toko
 * menyusul, lalu dipotong di batas aman.
 *
 * Tanpa isian admin, hasilnya `specific` apa adanya = perilaku lama, nol
 * regresi bagi toko yang membiarkannya kosong.
 */
export function pageSeoDescription(
  specific: string,
  storeTagline?: string | null
): string {
  const tag = (storeTagline ?? "").trim();
  if (!tag) return specific;
  return clampText(`${specific} ${tag}`, SEO_DESCRIPTION_MAX);
}

/**
 * Tanggal `lastmod` dari sebuah stempel waktu — `undefined` bila kosong atau
 * tak sah.
 *
 * KENAPA `undefined`, BUKAN "sekarang". Sampai 2026-09-12 `src/app/sitemap.ts`
 * memberi `new Date()` ke SETIAP URL, sehingga seluruh 117 URL sebuah toko
 * mengaku "baru saja berubah" pada SETIAP pengambilan sitemap (terverifikasi
 * live: 1 nilai lastmod unik untuk 117 URL). Google memakai `lastmod` hanya
 * selama ia terbukti akurat; sitemap yang selalu berkata "semua baru berubah"
 * mengajarinya mengabaikan sinyal itu — padahal sinyal itulah satu-satunya
 * mekanisme yang membuat perayapan ulang berjalan sendiri.
 *
 * `lastModified` yang ABSEN dibaca sebagai "tidak diketahui" dan tidak merusak
 * kepercayaan. Diam lebih baik daripada berbohong.
 */
export function lastModifiedDate(
  raw: string | null | undefined
): Date | undefined {
  const text = (raw ?? "").trim();
  if (!text) return undefined;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/**
 * Stempel waktu TERBARU dari sekumpulan item — dipakai halaman DAFTAR
 * (beranda, kategori, merek), yang memang berubah ketika isinya berubah.
 *
 * `undefined` bila tak satu pun item punya tanggal sah: kategori kosong tidak
 * boleh meminjam tanggal kategori lain.
 */
export function newestUpdatedAt(
  items: readonly { updatedAt?: string }[]
): Date | undefined {
  let newest: number | undefined;
  for (const item of items ?? []) {
    const date = lastModifiedDate(item?.updatedAt);
    if (!date) continue;
    const time = date.getTime();
    if (newest === undefined || time > newest) newest = time;
  }
  return newest === undefined ? undefined : new Date(newest);
}

/**
 * Bentuk token `google-site-verification` yang SAH untuk meta tag: huruf,
 * angka, `-`, `_`. Google memakai base64url; tak pernah ada spasi di dalamnya.
 */
const VERIFICATION_TOKEN_RE = /^[A-Za-z0-9_-]{10,128}$/;

/**
 * Rapikan isian token verifikasi Google Search Console.
 *
 * KENAPA PEMAAF, BUKAN KETAT. Google menampilkan token yang SAMA dalam dua
 * bentuk yang mirip, dan orang wajar menyalin yang salah:
 *
 *   meta tag  →  content="tu1IKNeX…"                    ← yang dipakai di sini
 *   DNS TXT   →  google-site-verification=tu1IKNeX…     ← bentuk lain
 *
 * Itu benar-benar terjadi (PROD 2026-09-12): nilai DNS tersimpan utuh, tag-nya
 * terbit sebagai `content="google-site-verification=tu1IK…"`, dan verifikasi
 * gagal SENYAP — tag ada, kelihatan benar, tak pernah cocok. Menolak isian yang
 * maksudnya sudah jelas tak menolong siapa pun, jadi awalan itu dibuang.
 *
 * Yang juga dimaafkan: seluruh baris `<meta …>` yang ter-copy utuh.
 *
 * Yang TIDAK dimaafkan: apa pun yang sesudah dirapikan bukan token
 * (mengandung spasi, tanda kutip, kurung siku). Itu memulangkan string KOSONG —
 * fail-closed: lebih baik meta-nya hilang daripada terbit rusak, karena tag
 * rusak terlihat persis seperti tag benar.
 */
export function normalizeSiteVerificationToken(
  raw: string | null | undefined
): string {
  let text = (raw ?? "").trim();
  if (!text) return "";
  // Seluruh baris <meta …> ter-copy: ambil isi content="…".
  const meta = text.match(/content\s*=\s*["']([^"']+)["']/i);
  if (meta) text = meta[1].trim();
  // Bentuk DNS TXT: buang awalannya.
  text = text.replace(/^google-site-verification\s*=\s*/i, "").trim();
  return VERIFICATION_TOKEN_RE.test(text) ? text : "";
}

/**
 * Pesan galat untuk layar admin — `null` bila isian sah (termasuk kosong,
 * yang berarti "tidak dipakai").
 *
 * Terpisah dari `normalizeSiteVerificationToken` karena keduanya menjawab
 * pertanyaan berbeda: yang itu "jadi apa nilainya", yang ini "apa yang perlu
 * diberitahukan ke orangnya". Menyatukannya memaksa salah satu berbohong.
 */
export function siteVerificationTokenError(
  raw: string | null | undefined
): string | null {
  const text = (raw ?? "").trim();
  if (!text) return null;
  if (normalizeSiteVerificationToken(text)) return null;
  if (/<\s*meta/i.test(text)) {
    return "Sepertinya seluruh baris <meta> ikut tersalin, tapi isinya tak terbaca. Salin hanya kode di dalam content=\"…\".";
  }
  if (/\s/.test(text)) {
    return "Kode verifikasi tidak boleh mengandung spasi. Salin hanya kodenya, tanpa teks lain.";
  }
  return "Kode verifikasi tidak dikenali. Di Search Console pilih metode Tag HTML, lalu salin kode di dalam content=\"…\".";
}

/**
 * Bentuk kode `facebook-domain-verification` yang sah: huruf kecil & angka
 * (contoh nyata: berkas verifikasi apex `/48t8tubw4vn3zkv3q3vrzcx8asadyp.html`,
 * 30 karakter). Rentang panjang dilonggarkan supaya kode Meta yang sedikit
 * berbeda panjangnya tidak ditolak diam-diam.
 */
const META_DOMAIN_VERIFICATION_RE = /^[a-z0-9]{10,100}$/;

/**
 * Rapikan isian kode verifikasi domain Meta (Pengaturan Bisnis → Keamanan
 * Merek → Domain → metode Tag meta). Pemaaf dengan alasan yang sama seperti
 * `normalizeSiteVerificationToken`: kode saja, seluruh baris `<meta …>`, atau
 * bentuk DNS TXT `facebook-domain-verification=…` — semuanya jadi kode saja.
 * Selain itu → "" (fail-closed: meta tidak dipancarkan, bukan terbit rusak).
 */
export function normalizeMetaDomainVerification(
  raw: string | null | undefined
): string {
  let text = (raw ?? "").trim();
  if (!text) return "";
  const meta = text.match(/content\s*=\s*["']([^"']+)["']/i);
  if (meta) text = meta[1].trim();
  text = text.replace(/^facebook-domain-verification\s*=\s*/i, "").trim();
  return META_DOMAIN_VERIFICATION_RE.test(text) ? text : "";
}

/** Pesan galat layar admin untuk kode verifikasi domain Meta; `null` = sah/kosong. */
export function metaDomainVerificationError(
  raw: string | null | undefined
): string | null {
  const text = (raw ?? "").trim();
  if (!text) return null;
  if (normalizeMetaDomainVerification(text)) return null;
  if (/<\s*meta/i.test(text)) {
    return "Sepertinya seluruh baris <meta> ikut tersalin, tapi isinya tak terbaca. Salin hanya kode di dalam content=\"…\".";
  }
  if (/\s/.test(text)) {
    return "Kode verifikasi tidak boleh mengandung spasi. Salin hanya kodenya, tanpa teks lain.";
  }
  return "Kode verifikasi tidak dikenali. Di Meta pilih metode Tag meta, lalu salin kode di dalam content=\"…\" (huruf kecil & angka).";
}

/**
 * Objek `verification` untuk metadata root layout — SATU objek UTUH berisi
 * Google DAN Meta. Next menggabung metadata antar-segmen secara DANGKAL:
 * `verification` milik segmen anak MENGGANTI seluruh milik layout, jadi kedua
 * tag wajib lahir di objek yang sama dan segmen anak toko tak boleh menulis
 * `verification` sendiri (dijaga `siteVerificationMetadata.test.ts`).
 * Keduanya kosong → `undefined` (tak ada tag verifikasi sama sekali).
 */
export function siteVerificationMetadata(input: {
  google: string;
  meta: string;
}): Metadata["verification"] | undefined {
  const google = input.google.trim();
  const meta = input.meta.trim();
  if (!google && !meta) return undefined;
  return {
    ...(google ? { google } : {}),
    ...(meta ? { other: { "facebook-domain-verification": meta } } : {}),
  };
}
